import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-cyan-500 to-teal-400">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: July 22, 2026</p>

          <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">1. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-700 mb-2">Account Information</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Email address (for authentication and alerts)</li>
                <li>Name (optional, via Google OAuth)</li>
                <li>Phone number (optional, for SMS alerts - Premium only)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-2 mt-4">Preferences Data</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Home location (ZIP code or city name with coordinates)</li>
                <li>Additional beach locations (Premium users)</li>
                <li>Weather preferences (temperature, wind, cloud cover, etc.)</li>
                <li>Alert settings (threshold, time of day, lead time)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-2 mt-4">Usage Data</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Login timestamps</li>
                <li>Feature usage (dashboard views, preference changes)</li>
                <li>Email/SMS delivery status</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-2 mt-4">Payment Information</h3>
              <p>
                Payment processing is handled by Stripe. We do NOT store credit card numbers. Stripe may store payment
                information according to their privacy policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">2. How We Use Your Information</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>Fetch weather forecasts for your locations</li>
                <li>Calculate beach day scores based on your preferences</li>
                <li>Send email or SMS alerts when conditions match your criteria</li>
                <li>Process premium subscriptions</li>
                <li>Improve our Service and user experience</li>
                <li>Communicate with you about your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">3. Data Sharing</h2>
              <p>We share data with the following third parties:</p>

              <h3 className="text-xl font-semibold text-gray-700 mb-2 mt-4">Service Providers</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Firebase/Google Cloud:</strong> Database, authentication, hosting</li>
                <li><strong>Open-Meteo:</strong> Weather forecast data (no personal data shared)</li>
                <li><strong>Stormglass:</strong> Ocean conditions data (coordinates only, no personal data)</li>
                <li><strong>SendGrid:</strong> Email delivery (email addresses only)</li>
                <li><strong>Twilio:</strong> SMS delivery (phone numbers only, Premium users)</li>
                <li><strong>Stripe:</strong> Payment processing (payment information only)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-2 mt-4">We Do NOT</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Sell your personal information to third parties</li>
                <li>Share your data with advertisers</li>
                <li>Use your data for purposes other than providing the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">4. Data Storage & Security</h2>
              <p>
                Your data is stored in Google Cloud Firestore with encryption at rest. We use industry-standard security
                practices including:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>HTTPS/TLS encryption for all data transmission</li>
                <li>Firebase Authentication for secure login</li>
                <li>API keys stored in Google Secret Manager</li>
                <li>Regular security updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">5. Data Retention</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Account data:</strong> Retained while your account is active</li>
                <li><strong>Email records:</strong> Automatically deleted after 7 days</li>
                <li><strong>SMS records:</strong> Retained by Twilio per their policy</li>
                <li><strong>Payment data:</strong> Retained by Stripe per their policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Access:</strong> View all data we have about you</li>
                <li><strong>Update:</strong> Change your preferences at any time</li>
                <li><strong>Delete:</strong> Request account deletion (contact us)</li>
                <li><strong>Export:</strong> Request a copy of your data</li>
                <li><strong>Opt-out:</strong> Disable email/SMS alerts or cancel subscription</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">7. SMS Privacy & Consent</h2>
              <p>
                When you opt into SMS alerts, your phone number is:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Stored encrypted in Firebase</li>
                <li>Shared with Twilio only for message delivery</li>
                <li>Never sold or shared with marketers</li>
                <li>Removable at any time by disabling SMS in Settings or replying STOP</li>
              </ul>

              <p className="mt-4 font-semibold text-gray-800">
                Mobile Information Sharing Policy:
              </p>
              <p className="mt-2 bg-blue-50 border-l-4 border-blue-500 p-4 text-gray-700">
                <strong>Your mobile phone number and SMS messaging consent information will not be shared with
                third parties or affiliates for marketing or promotional purposes.</strong> We only use your phone
                number to deliver the beach weather alerts you explicitly requested. Your consent to receive SMS
                messages is stored securely and used solely for operating our alert service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">8. Cookies & Tracking</h2>
              <p>
                We use minimal tracking:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Authentication cookies:</strong> Required for login (Firebase)</li>
                <li><strong>Session storage:</strong> To remember your preferences during your session</li>
                <li><strong>No advertising trackers</strong></li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">9. Children's Privacy</h2>
              <p>
                Our Service is not directed to children under 13. We do not knowingly collect information from children under 13.
                If you believe a child has provided us with personal information, contact us and we will delete it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">10. International Users</h2>
              <p>
                Your data may be processed in the United States where our servers are located. By using the Service,
                you consent to the transfer of your information to the U.S.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">11. Changes to Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes via email
                or a notice on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">12. Contact Us</h2>
              <p>
                For privacy questions, data requests, or to exercise your rights, contact us at:{' '}
                <a href="mailto:privacy@10outtaten.com" className="text-cyan-600 hover:underline">
                  privacy@10outtaten.com
                </a>
              </p>
              <p className="mt-2">
                Or via mail:<br />
                10/10 Beach Day Alerts<br />
                [Your Business Address]<br />
                United States
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
